import {
  Box,
  Flex,
  Modal,
  ModalContent,
  ModalOverlay,
  Spinner,
} from "@chakra-ui/react";
import { Suspense, useEffect, useRef, useState } from "react";
import { isMobile, isTablet, isAndroid } from "react-device-detect";
import { Outlet } from "react-router";
import { useIsSidebarOpen } from "../../hooks/useIsSidebarOpen";
import { useIsOverlayOpen } from "../../screens/reader/hooks/hookstate/useIsOverlayOpen";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { NavBar } from "../navigation/NavBar";
import { SideBar } from "../navigation/SideBar";
import { ErrorBoundary } from "../errors/ErrorBoundary";
import { FunnelModal } from "../funnel/FunnelModal";
import { useGaTracking } from "../../hooks/useGaTracking";
import { CollapsibleNavBar } from "../navbar/CollapsibleNavBar";
import { SomethingWentWrong } from "../errors/components/SomethingWentWrong";
import { setUser } from "@sentry/react";
import { useAccountData } from "../../hooks/useAccountData";
import { SlideInMenuCentered } from "../slideInMenu/SlideInMenuCentered";
import { SlideInMenuSidebar } from "../slideInMenu/SlideInMenuSidebar";
import { useSlideInMenu } from "../slideInMenu/hooks/useSlideInMenu";
import { useModalRouting } from "../slideInMenu/hooks/useModalRouting";
import { EmailVerificationBanner } from "../../screens/root/components/EmailVerificationBanner";
import { OnboardingBanner } from "../../screens/root/components/OnboardingBanner";
import {
  DEFAULT_SIDEBAR_WIDTH,
  Z_INDEX_NAVBAR,
} from "../../screens/root/constants";
import {
  useSearchParams,
  useLocation,
  ScrollRestoration,
} from "react-router-dom";
import { rootComponentsStore } from "../../hookstate/rootComponentsStore";
import { AppPromptDrawer } from "../../screens/root/components/AppPromptDrawer";
import { useDisableBodyScroll } from "../../hooks/useDisableBodyScroll";
import { useTopBarHeight } from "../../hooks/userTopBarHeight";
import { useIsTopBarHidden } from "../../hooks/useIsTopBarHidden";
import { useIsInReader } from "../../hooks/useIsInReader";
import { isReaderRoute } from "../../utils/isReaderRoute";
import { useNavigationHistory } from "../../hooks/useNavigationHistory";
import { useCloseModalsOnReaderChange } from "../../hooks/useCloseModalsOnReaderChange";
import { SearchV2Provider } from "../../screens/search/hooks/useSearchContext";
import { useQueryClient } from "@tanstack/react-query";
import { useRootStripeCheckoutReturns } from "../payments/hooks/useRootStripeCheckoutReturns";
import { StripePaymentSettlingBanner } from "../payments/components/StripePaymentSettlingBanner";

// Routes that use virtualized lists handle their own scroll restoration
// via useVirtualScrollRestoration hook - ScrollRestoration should skip them
const isVirtualizedRoute = (pathname: string) =>
  pathname === "/" ||
  pathname === "/originals" ||
  pathname.startsWith("/channel/") ||
  pathname === "/library" ||
  pathname.startsWith("/browse") ||
  pathname.startsWith("/books") ||
  pathname.startsWith("/creators") ||
  pathname.startsWith("/bundles") ||
  pathname.startsWith("/cms/");

function shouldDisablePostAuthPromosForE2E(): boolean {
  try {
    return localStorage.getItem("gc_e2e_disable_post_auth_promos") === "1";
  } catch {
    return false;
  }
}

export function RootLayout() {
  useGaTracking(true);
  useNavigationHistory();
  useCloseModalsOnReaderChange();
  const queryClient = useQueryClient();
  useModalRouting();

  const location = useLocation();
  const isSmallScreen = useIsSmallBreakpoint();

  const { openMenu, onCloseMenu } = useSlideInMenu();
  const { isSidebarOpen, setIsSidebarOpen } = useIsSidebarOpen();
  const { isOverlayOpen } = useIsOverlayOpen();
  const isReader = useIsInReader();
  const topBarHeight = useTopBarHeight();
  const isTopBarHidden = useIsTopBarHidden();
  const collapseSidebarForSmalldevices = isSmallScreen || isMobile || isTablet;
  const [defaultedSidebar, setDefaultedSidebar] = useState(false);

  const [searchParams] = useSearchParams();
  const isStandalone = searchParams.get("standalone") === "1";
  const isBlackFridayPurchaseMode = searchParams.get("purchase") === "true";

  useEffect(() => {
    setDefaultedSidebar(true);
    if (defaultedSidebar) return;
    // Always close sidebar when entering reader
    if (isReader) {
      setIsSidebarOpen(false);
      return;
    }
    // For mobile/tablet, close sidebar by default
    if (collapseSidebarForSmalldevices) {
      setIsSidebarOpen(false);
    }
  }, [
    collapseSidebarForSmalldevices,
    defaultedSidebar,
    isReader,
    setIsSidebarOpen,
  ]);

  useEffect(() => {
    if (!isReader) return;
    rootComponentsStore.isSidebarOpen.set(false);
  }, [isReader]);

  // Close slide-in menu on navigation
  const prevMenuLocationRef = useRef<string | null>(null);

  useEffect(() => {
    // Only close menu when navigating to a different page, not when changing reading modes or P2P
    if (
      openMenu &&
      prevMenuLocationRef.current !== null &&
      location.pathname !== prevMenuLocationRef.current
    ) {
      const isInReader = isReaderRoute(location.pathname);
      const wasInReader = isReaderRoute(prevMenuLocationRef.current);

      if (isInReader && wasInReader) {
        // Both are reader paths - check if it's the same release
        const getReleaseKey = (path: string) => path.split("/")[2]; // /read/RELEASE_KEY/...
        const prevReleaseKey = getReleaseKey(prevMenuLocationRef.current);
        const currentReleaseKey = getReleaseKey(location.pathname);

        // Close menu if navigating to a different release
        if (prevReleaseKey !== currentReleaseKey) {
          onCloseMenu();
        }
      } else {
        // Navigating to/from reader or different page entirely
        onCloseMenu();
      }
    }
    prevMenuLocationRef.current = location.pathname;
  }, [location.pathname, openMenu, onCloseMenu]);

  const accountData = useAccountData();
  const userData = accountData?.user;
  useRootStripeCheckoutReturns({ queryClient });

  useEffect(() => {
    if (accountData) {
      try {
        setUser({
          id: accountData.user?.id,
          email: accountData.user?.email_address,
          guest_id: accountData.guest_id,
        });
      } catch {
        // Sentry may be blocked by ad blockers
      }
    }
  }, [accountData]);

  useDisableBodyScroll();

  // Render only after  initialization
  if (!defaultedSidebar) return null;

  const isValkyraePage = location.pathname.includes("valkyrae");
  const isAboutPage = location.pathname === "/about" || location.pathname.startsWith("/about/");
  const isSettingsAccountPage =
    location.pathname.includes("settings") ||
    location.pathname.includes("settings/account");
  const isGoldPage = location.pathname.startsWith("/gold");
  const isHolidayBundlePage = location.pathname.includes("holiday-bundle");
  const isUltimateXmenBundlePage = location.pathname.includes(
    "ultimate-x-men-bundle",
  );
  const isBlackFridayGoldPageEnabled =
    accountData?.features["black_friday_deal"];
  const isBlackFridayDeal = isGoldPage && isBlackFridayGoldPageEnabled;
  const isGiftCardPage = location.pathname.includes("gift-card");
  const isGoldRewardsPage =
    location.pathname.startsWith("/gold-rewards") ||
    location.pathname.startsWith("/my-shelf/gold-rewards");
  const disablePostAuthPromosForE2E = shouldDisablePostAuthPromosForE2E();
  const showEmailVerificationBanner =
    userData &&
    !userData.verified &&
    !disablePostAuthPromosForE2E &&
    !isBlackFridayDeal &&
    !isHolidayBundlePage &&
    !isUltimateXmenBundlePage &&
    !isSettingsAccountPage;
  // Users who completed the classic onboarding (onboarding === "completed")
  // may not have an archetype yet — the new onboarding flow (gated by the
  // new_onboarding feature flag) asks for one. Show the banner for those
  // users too so they can finish picking an archetype; matches the
  // precondition the sidebar uses for its `hasNewOnboarding` link.
  const needsArchetype =
    !!accountData?.features?.["new_onboarding"] && !accountData?.archetype;
  const showOnboardingBanner =
    userData &&
    (userData.onboarding !== "completed" || needsArchetype) &&
    !disablePostAuthPromosForE2E &&
    !isBlackFridayDeal &&
    !isHolidayBundlePage &&
    !isUltimateXmenBundlePage &&
    !isSettingsAccountPage;
  const shouldHideBanners = isReader;

  const showSidebar =
    !isValkyraePage &&
    !isGoldPage &&
    !isStandalone &&
    !isBlackFridayDeal &&
    !isHolidayBundlePage &&
    !isUltimateXmenBundlePage &&
    !isAboutPage;

  return (
    <SearchV2Provider>
    <ErrorBoundary errorComponent={<SomethingWentWrong />}>
      {/* ScrollRestoration handles non-virtualized routes.
          Virtualized routes (home, library, browse) use useVirtualScrollRestoration hook.
          Reader manages its own scroll. */}
      <ScrollRestoration
        getKey={(location) => {
          // Skip scroll restoration for virtualized routes and reader
          // (they handle their own scroll restoration)
          if (isVirtualizedRoute(location.pathname)) return null;
          if (isReaderRoute(location.pathname)) return null;
          // Default: restore based on pathname
          return location.pathname;
        }}
      />
      <Flex
        direction="column"
        // h="100dvh"
        data-is-android={isAndroid}
        // overflow="hidden"
        position="relative"
      >
        <FunnelModal />
        <StripePaymentSettlingBanner />
        {!isStandalone && !isBlackFridayPurchaseMode &&
          (isReader ? (
            <CollapsibleNavBar
              isCollapsed={!isOverlayOpen}
              shouldCollapse
              boxProps={{
                width: "100%",
                zIndex: isOverlayOpen ? Z_INDEX_NAVBAR : 0,
                position: "fixed",
                top: "0",
                backgroundColor: "charcoal.charcoal",
              }}
            >
              <NavBar />
            </CollapsibleNavBar>
          ) : (
            <Box
              width="100%"
              zIndex={2}
              position="sticky"
              top="0"
              backgroundColor="charcoal.charcoal"
              transform={
                isTopBarHidden ? "translateY(-100%)" : "translateY(0)"
              }
              transition="transform 0.1s ease-out"
            >
              <NavBar />
            </Box>
          ))}
        {!isHolidayBundlePage &&
          !isUltimateXmenBundlePage &&
          !isValkyraePage &&
          !isBlackFridayDeal &&
          !isSettingsAccountPage &&
          !isGoldRewardsPage &&
          accountData && <AppPromptDrawer />}
        <Flex
          position="fixed"
          top={0}
          left={0}
          width="100%"
          height="100%"
          background="transparent.black.70"
          zIndex={2}
          transition="all .4s ease"
          opacity={openMenu ? 1 : 0}
          visibility={openMenu ? "visible" : "hidden"}
          onClick={onCloseMenu}
        />
        <SlideInMenuSidebar
          active={isReader}
          openMenu={openMenu}
          onCloseMenu={onCloseMenu}
        />
        <SlideInMenuCentered
          active={!isReader}
          openMenu={openMenu}
          onCloseMenu={onCloseMenu}
        />

        <Flex gap={isGoldPage ? undefined : 6}>
          {showSidebar &&
            (isOverlayOpen || isSmallScreen ? (
              <Modal
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
                onOverlayClick={() => setIsSidebarOpen(false)}
                motionPreset="slideInLeft"
                trapFocus={true}
                blockScrollOnMount={false}
                variant="sidebar"
              >
                <ModalOverlay
                  pointerEvents={isOverlayOpen ? "none" : "auto"}
                  width={isOverlayOpen ? "260px" : "100%"}
                  height={`calc(100vh)`}
                  bg={isOverlayOpen ? "charcoal.charcoal" : "blackAlpha.50"}
                  onClick={() => setIsSidebarOpen(false)}
                />
                <ModalContent
                  onClick={() => setIsSidebarOpen(false)}
                  containerProps={{
                    pointerEvents: "none",
                  }}
                  height={`calc(100% - ${topBarHeight.get()}px)`}
                  p={0}
                  m={0}
                >
                  <Box
                    className="styled-scrollbar"
                    sx={{ scrollbarGutter: "stable" }}
                    pointerEvents="all"
                    width={isOverlayOpen ? "100%" : "80vw"}
                    maxWidth="400px"
                    height={"100%"}
                    pl={isOverlayOpen ? 8 : 4}
                    pr={0}
                    overflowY="auto"
                    zIndex={3}
                    background={
                      !isSmallScreen && isOverlayOpen
                        ? "charcoal.charcoal"
                        : "black"
                    }
                  >
                    <SideBar />
                  </Box>
                </ModalContent>
              </Modal>
            ) : isReader ? null : location.pathname.startsWith(
                "/admin-next",
              ) ? null : (
              <Box
                className="styled-scrollbar-hover"
                sx={{ scrollbarGutter: "stable" }}
                display={!isGoldPage ? "block" : "none"}
                width={isSidebarOpen ? `${DEFAULT_SIDEBAR_WIDTH}px` : "78px"}
                ml={8}
                height="100%"
                maxHeight={`calc(100vh - ${topBarHeight.get()}px)`}
                top={`${topBarHeight.get()}px`}
                position="sticky"
                overflowY="auto"
              >
                <SideBar collapsed={!isSidebarOpen} />
              </Box>
            ))}

          <Flex
            flex={1}
            minWidth={0}
            minHeight={`calc(100dvh - ${topBarHeight.get()}px)`}
            sx={{
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Suspense
              fallback={
                <Flex flex={1} align="center" justify="center" w="100%">
                  <Spinner color="blaze.blaze" size="xl" />
                </Flex>
              }
            >
              <Outlet />
            </Suspense>
          </Flex>
        </Flex>
        <Box zIndex={isGoldPage || isGiftCardPage ? 2 : undefined}>
          {showEmailVerificationBanner && !shouldHideBanners && (
            <EmailVerificationBanner emailAddress={userData.email_address} />
          )}
          {showOnboardingBanner &&
            !showEmailVerificationBanner &&
            !shouldHideBanners && <OnboardingBanner />}
        </Box>
      </Flex>
    </ErrorBoundary>
    </SearchV2Provider>
  );
}
